/**
 * Iter Feb 2026 R53 — Super Admin · AI Usage & Workspace Analytics
 *
 * Three sections:
 *   1. Per-workspace AI usage roll-up for a chosen month.
 *   2. Drilldown into a single workspace: per-user AI spend + a modal
 *      to edit soft/hard caps at the workspace and user level.
 *   3. Workspace analytics — DAU / WAU / MAU + record counts per module
 *      + current-month AI spend. Meant to help price billing per WS.
 */
import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import PageHeader from "@/components/PageHeader";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Loader2, RefreshCw, DollarSign, Users, ChevronRight, Save } from "lucide-react";
import { apiErrorMessage } from "@/lib/apiError";

function fmtUsd(cents) {
  return `$${((cents || 0) / 100).toFixed(2)}`;
}

function currentMonth() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}

export default function AdminAiUsage() {
  const [month, setMonth] = useState(currentMonth());
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [drillWsId, setDrillWsId] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/super-admin/ai-usage/summary?month=${month}`);
      setSummary(data);
    } catch (e) {
      toast.error(apiErrorMessage(e, "Failed to load AI usage summary"));
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [month]);

  const totals = summary?.totals || {};

  return (
    <div className="space-y-6" data-testid="admin-ai-usage-page">
      <PageHeader eyebrow="Super Admin" title="AI Usage & Workspace Pricing"
        description="Per-workspace + per-user AI spend, caps enforcement, and workspace-level record-count analytics for billing." />

      <div className="flex items-center gap-2">
        <label className="text-xs font-semibold uppercase tracking-widest">Month</label>
        <Input type="month" value={month} onChange={(e) => setMonth(e.target.value)}
                className="h-9 rounded-none w-40" data-testid="admin-ai-month" />
        <Button variant="outline" size="sm" onClick={load} className="rounded-none h-9" data-testid="admin-ai-refresh">
          <RefreshCw className="w-3.5 h-3.5 mr-1" /> Refresh
        </Button>
        <div className="ml-auto text-xs text-muted-foreground">
          <span className="mr-4"><b>{totals.workspaces || 0}</b> workspaces</span>
          <span className="mr-4"><b>{totals.calls || 0}</b> calls</span>
          <span><b>${(totals.cost_usd_est || 0).toFixed(2)}</b> estimated spend</span>
        </div>
      </div>

      <Card className="rounded-none">
        <CardContent className="p-0">
          <table className="w-full text-sm">
            <thead className="bg-muted text-[11px] uppercase tracking-widest text-muted-foreground">
              <tr>
                <th className="text-left px-3 py-2">Workspace</th>
                <th className="text-right px-3 py-2">Calls</th>
                <th className="text-right px-3 py-2">Users</th>
                <th className="text-right px-3 py-2">Tokens</th>
                <th className="text-right px-3 py-2">Est. cost</th>
                <th className="text-right px-3 py-2">Cap</th>
                <th className="text-right px-3 py-2">Errors</th>
                <th className="px-3 py-2 w-8"></th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={8} className="text-center py-8 text-muted-foreground">
                  <Loader2 className="w-4 h-4 mx-auto animate-spin" />
                </td></tr>
              )}
              {!loading && (summary?.workspaces || []).length === 0 && (
                <tr><td colSpan={8} className="text-center py-8 text-muted-foreground">
                  No AI activity yet in {month}.
                </td></tr>
              )}
              {!loading && (summary?.workspaces || []).map((w) => {
                const capped = w.hard_cap_cents && w.cost_cents_est >= w.hard_cap_cents;
                return (
                  <tr key={w.workspace_id} className={`border-t border-border hover:bg-muted/40 cursor-pointer ${capped ? "bg-rose-50" : ""}`}
                       onClick={() => setDrillWsId(w.workspace_id)}
                       data-testid={`admin-ai-ws-row-${w.workspace_id}`}>
                    <td className="px-3 py-2 font-medium">{w.workspace_name}</td>
                    <td className="px-3 py-2 text-right">{w.calls}</td>
                    <td className="px-3 py-2 text-right">{w.unique_users}</td>
                    <td className="px-3 py-2 text-right text-xs text-muted-foreground">{w.tokens_in.toLocaleString()} / {w.tokens_out.toLocaleString()}</td>
                    <td className="px-3 py-2 text-right font-mono font-semibold">
                      ${w.cost_usd_est.toFixed(2)}
                    </td>
                    <td className="px-3 py-2 text-right text-xs">
                      {w.hard_cap_cents ? fmtUsd(w.hard_cap_cents) : <span className="text-muted-foreground">—</span>}
                    </td>
                    <td className="px-3 py-2 text-right">
                      {w.errors > 0 ? <span className="text-rose-600 font-semibold">{w.errors}</span> : "0"}
                    </td>
                    <td className="px-3 py-2 text-right">
                      <ChevronRight className="w-3.5 h-3.5 text-muted-foreground" />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </CardContent>
      </Card>

      {drillWsId && (
        <WorkspaceDrill wsId={drillWsId} month={month}
                        onClose={() => { setDrillWsId(null); load(); }} />
      )}
    </div>
  );
}


function WorkspaceDrill({ wsId, month, onClose }) {
  const [tab, setTab] = useState("users");
  const [users, setUsers] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [caps, setCaps] = useState(null);
  const [saving, setSaving] = useState(false);

  const loadUsers = async () => {
    const { data } = await api.get(`/super-admin/ai-usage/workspaces/${wsId}/users?month=${month}`);
    setUsers(data);
  };
  const loadAnalytics = async () => {
    const { data } = await api.get(`/super-admin/workspace-analytics/${wsId}`);
    setAnalytics(data);
  };
  const loadCaps = async () => {
    const { data } = await api.get(`/super-admin/ai-usage/workspaces/${wsId}/caps`);
    setCaps({
      workspace_soft_cap_cents: data.workspace_soft_cap_cents,
      workspace_hard_cap_cents: data.workspace_hard_cap_cents,
      user_caps: Object.entries(data.user_caps || {}).map(([user_id, v]) => ({
        user_id, ...v,
      })),
    });
  };

  useEffect(() => { loadUsers(); loadAnalytics(); loadCaps(); /* eslint-disable-next-line */ }, [wsId, month]);

  const saveCaps = async () => {
    setSaving(true);
    try {
      await api.put(`/super-admin/ai-usage/workspaces/${wsId}/caps`, {
        workspace_soft_cap_cents: caps.workspace_soft_cap_cents || null,
        workspace_hard_cap_cents: caps.workspace_hard_cap_cents || null,
        user_caps: (caps.user_caps || []).filter(u => u.user_id),
      });
      toast.success("Caps saved");
    } catch (e) { toast.error(apiErrorMessage(e, "Save failed")); }
    finally { setSaving(false); }
  };

  const totalUsd = useMemo(() => (users?.users || []).reduce((s, u) => s + (u.cost_usd_est || 0), 0), [users]);

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-5xl max-h-[92vh] overflow-y-auto rounded-none" data-testid="admin-ai-drill-dialog">
        <DialogHeader>
          <DialogTitle>
            {analytics?.workspace_name || wsId} · AI + Analytics · {month}
          </DialogTitle>
        </DialogHeader>

        <div className="flex gap-1 border-b border-border">
          {[{k: "users", label: `Users · $${totalUsd.toFixed(2)}`},
             {k: "analytics", label: "Workspace analytics"},
             {k: "caps", label: "Caps"}].map((t) => (
            <button key={t.k} onClick={() => setTab(t.k)}
                     className={`px-3 py-2 text-xs uppercase tracking-widest font-semibold ${tab === t.k ? "border-b-2 border-primary text-primary" : "text-muted-foreground"}`}
                     data-testid={`admin-ai-drill-tab-${t.k}`}>{t.label}</button>
          ))}
        </div>

        {tab === "users" && (
          <div className="pt-3">
            <table className="w-full text-sm">
              <thead className="bg-muted text-[10px] uppercase tracking-widest text-muted-foreground">
                <tr>
                  <th className="text-left px-3 py-2">User</th>
                  <th className="text-right px-3 py-2">Calls</th>
                  <th className="text-right px-3 py-2">Tokens</th>
                  <th className="text-right px-3 py-2">Cost</th>
                  <th className="text-left px-3 py-2">Top feature</th>
                </tr>
              </thead>
              <tbody>
                {(users?.users || []).map((u) => {
                  const topFeat = Object.entries(u.features || {}).sort((a, b) => b[1] - a[1])[0]?.[0] || "—";
                  return (
                    <tr key={u.user_id} className="border-t border-border">
                      <td className="px-3 py-2">
                        <div className="font-medium">{u.name}</div>
                        {u.email && <div className="text-[10px] text-muted-foreground">{u.email}</div>}
                      </td>
                      <td className="px-3 py-2 text-right">{u.calls}</td>
                      <td className="px-3 py-2 text-right text-xs">{u.tokens_in.toLocaleString()} / {u.tokens_out.toLocaleString()}</td>
                      <td className="px-3 py-2 text-right font-mono font-semibold">${u.cost_usd_est.toFixed(2)}</td>
                      <td className="px-3 py-2 text-xs">{topFeat}</td>
                    </tr>
                  );
                })}
                {(users?.users || []).length === 0 && (
                  <tr><td colSpan={5} className="text-center py-6 text-muted-foreground">No user activity in this month.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        )}

        {tab === "analytics" && analytics && (
          <div className="pt-3 space-y-4">
            <div className="grid grid-cols-4 gap-3">
              {[{label: "DAU", v: analytics.active_users?.dau},
                {label: "WAU", v: analytics.active_users?.wau},
                {label: "MAU", v: analytics.active_users?.mau},
                {label: "AI spend (this mo)", v: `$${(analytics.ai_current_month?.cost_usd_est || 0).toFixed(2)}`}].map((k) => (
                <div key={k.label} className="border border-border p-3">
                  <div className="text-[10px] uppercase tracking-widest text-muted-foreground">{k.label}</div>
                  <div className="text-2xl font-bold">{k.v ?? 0}</div>
                </div>
              ))}
            </div>
            <div>
              <div className="text-xs font-semibold uppercase tracking-widest mb-2">Record counts per module</div>
              <table className="w-full text-sm">
                <thead className="bg-muted text-[10px] uppercase tracking-widest">
                  <tr><th className="text-left px-3 py-2">Module</th><th className="text-right px-3 py-2">Count</th></tr>
                </thead>
                <tbody>
                  {(analytics.module_counts || []).map((m) => (
                    <tr key={m.collection} className="border-t border-border">
                      <td className="px-3 py-2">{m.module}</td>
                      <td className="px-3 py-2 text-right font-mono">{(m.count || 0).toLocaleString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {tab === "caps" && caps && (
          <div className="pt-3 space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-semibold uppercase tracking-widest">Workspace soft cap · USD</label>
                <Input type="number" step="0.01"
                        value={caps.workspace_soft_cap_cents != null ? caps.workspace_soft_cap_cents / 100 : ""}
                        onChange={(e) => setCaps({...caps, workspace_soft_cap_cents: e.target.value ? Number(e.target.value) * 100 : null})}
                        placeholder="e.g. 50.00 (warns only)"
                        className="rounded-none" data-testid="admin-ai-cap-ws-soft" />
              </div>
              <div>
                <label className="text-xs font-semibold uppercase tracking-widest">Workspace hard cap · USD</label>
                <Input type="number" step="0.01"
                        value={caps.workspace_hard_cap_cents != null ? caps.workspace_hard_cap_cents / 100 : ""}
                        onChange={(e) => setCaps({...caps, workspace_hard_cap_cents: e.target.value ? Number(e.target.value) * 100 : null})}
                        placeholder="e.g. 100.00 (blocks)"
                        className="rounded-none" data-testid="admin-ai-cap-ws-hard" />
              </div>
            </div>
            <div>
              <div className="text-xs font-semibold uppercase tracking-widest mb-2">Per-user caps</div>
              {(caps.user_caps || []).map((uc, i) => (
                <div key={i} className="grid grid-cols-4 gap-2 items-end mb-2">
                  <Input value={uc.user_id}
                          onChange={(e) => {
                            const list = [...caps.user_caps]; list[i] = {...uc, user_id: e.target.value};
                            setCaps({...caps, user_caps: list});
                          }}
                          placeholder="user_id" className="rounded-none col-span-2" />
                  <Input type="number" step="0.01" placeholder="Soft USD"
                          value={uc.soft_cap_cents != null ? uc.soft_cap_cents / 100 : ""}
                          onChange={(e) => {
                            const list = [...caps.user_caps];
                            list[i] = {...uc, soft_cap_cents: e.target.value ? Number(e.target.value) * 100 : null};
                            setCaps({...caps, user_caps: list});
                          }}
                          className="rounded-none" />
                  <Input type="number" step="0.01" placeholder="Hard USD"
                          value={uc.hard_cap_cents != null ? uc.hard_cap_cents / 100 : ""}
                          onChange={(e) => {
                            const list = [...caps.user_caps];
                            list[i] = {...uc, hard_cap_cents: e.target.value ? Number(e.target.value) * 100 : null};
                            setCaps({...caps, user_caps: list});
                          }}
                          className="rounded-none" />
                </div>
              ))}
              <Button size="sm" variant="outline" className="rounded-none"
                       onClick={() => setCaps({...caps, user_caps: [...(caps.user_caps || []), {user_id: "", soft_cap_cents: null, hard_cap_cents: null}]})}
                       data-testid="admin-ai-cap-add-user">+ Add per-user cap</Button>
            </div>
            <div className="flex justify-end">
              <Button onClick={saveCaps} disabled={saving} className="rounded-none" data-testid="admin-ai-cap-save">
                <Save className="w-3.5 h-3.5 mr-1" /> {saving ? "Saving…" : "Save caps"}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
